import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  TableSortLabel,
  Box,
  Typography
} from '@material-ui/core';
import { Skeleton } from 'antd';
import './datagrid-custom.css';

interface Column {
  title: string;
  dataIndex: string;
  key: string;
  render?: (text: any, record: any) => React.ReactNode;
  sorter?: boolean;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

interface DataGridProps {
  columns: Column[];
  data: any[];
  total: number;
  page: number;
  pageSize: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowSelect?: (selectedRows: any[]) => void;
  selectable?: boolean;
  rowKey?: string | ((record: any) => string);
  style?: React.CSSProperties;
  rowClassName?: string | ((record: any) => string);
  emptyText?: string;
  className?: string;
  hidePagination?: boolean;
  selectedRows?: any[];
  selectedAll?: boolean;
}

type Order = 'asc' | 'desc';

const DataGrid: React.FC<DataGridProps> = ({
  columns,
  data,
  total,
  page,
  pageSize,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onRowSelect,
  selectable = false,
  rowKey = 'id',
  style,
  rowClassName,
  emptyText = 'No data available',
  className = '',
  hidePagination = false,
  selectedRows,
  selectedAll
}) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [selected, setSelected] = useState<string[]>([]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map(row => getRowKey(row));
      setSelected(newSelected);
      onRowSelect?.(data);
      return;
    }
    setSelected([]);
    onRowSelect?.([]);
  };

  const handleClick = (_: React.MouseEvent<unknown>, row: any) => {
    const rowKeyValue = getRowKey(row);
    const selectedIndex = selected.indexOf(rowKeyValue);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, rowKeyValue);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    const selectedRows = data.filter(row =>
      newSelected.includes(getRowKey(row))
    );
    onRowSelect?.(selectedRows);
  };

  const getRowKey = (record: any): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return record[rowKey] || record.id || Math.random().toString();
  };

  const isSelected = (row: any) => {
    if (selectedRows) {
      return selectedRows.some(
        selectedRow => getRowKey(selectedRow) === getRowKey(row)
      );
    }
    return selected.indexOf(getRowKey(row)) !== -1;
  };

  const getRowClassName = (record: any): string => {
    if (typeof rowClassName === 'function') {
      return rowClassName(record);
    }
    return rowClassName || '';
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) {
        return order === 'desc' ? 1 : -1;
      }
      if (aValue > bValue) {
        return order === 'desc' ? -1 : 1;
      }
      return 0;
    });
  }, [data, orderBy, order]);

  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const renderCell = (column: Column, record: any) => {
    const value = record[column.dataIndex];

    if (loading) {
      return <Skeleton active={true} paragraph={{ rows: 0, width: '100%' }} />;
    }

    if (column.render) {
      return column.render(value, record);
    }

    return value;
  };

  return (
    <Paper style={style} className={`datagrid-container ${className}`}>
      <div className="datagrid-scroll-x">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow className="datagrid-header">
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    className="datagrid-cell datagrid-checkbox-cell"
                    style={{ width: '60px' }}
                  >
                    <Checkbox
                      indeterminate={
                        selectedAll! &&
                        selected.length > 0 &&
                        selected.length < data.length
                      }
                      checked={
                        selectedAll! &&
                        data.length > 0 &&
                        selected.length === data.length
                      }
                      onChange={handleSelectAllClick}
                      color="primary"
                    />
                  </TableCell>
                )}
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    style={{ width: column.width }}
                    sortDirection={orderBy === column.dataIndex ? order : false}
                    className="datagrid-cell"
                  >
                    {column.sorter ? (
                      <TableSortLabel
                        active={orderBy === column.dataIndex}
                        direction={orderBy === column.dataIndex ? order : 'asc'}
                        onClick={() => handleRequestSort(column.dataIndex)}
                      >
                        {column.title}
                      </TableSortLabel>
                    ) : (
                      column.title
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={{ minHeight: 100 }}>
              {loading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`} className="datagrid-row">
                    {selectable && (
                      <TableCell padding="checkbox" className="datagrid-cell">
                        <Skeleton
                          active={true}
                          paragraph={{ rows: 0, width: '100%' }}
                        />
                      </TableCell>
                    )}
                    {columns.map(column => (
                      <TableCell
                        key={`skeleton-${column.key}-${index}`}
                        className="datagrid-cell"
                      >
                        <Skeleton
                          active={true}
                          paragraph={{ rows: 0, width: '100%' }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sortedData.length === 0 ? (
                <TableRow className="datagrid-row">
                  <TableCell
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    align="center"
                    className="datagrid-cell"
                  >
                    <Box py={4}>
                      <Typography variant="body2" color="textSecondary">
                        {emptyText}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row, index) => {
                  const isItemSelected = isSelected(row);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={event => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={getRowKey(row)}
                      selected={isItemSelected}
                      className={`datagrid-row ${getRowClassName(row)}`}
                    >
                      {selectable && (
                        <TableCell
                          padding="checkbox"
                          className="datagrid-cell datagrid-checkbox-cell"
                          style={{ width: '60px' }}
                        >
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': labelId }}
                            color="primary"
                          />
                        </TableCell>
                      )}
                      {columns.map(column => (
                        <TableCell
                          key={column.key}
                          align={column.align || 'left'}
                          style={{ width: column.width }}
                          className="datagrid-cell"
                        >
                          {renderCell(column, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {!hidePagination && (
        <TablePagination
          rowsPerPageOptions={[25, 50, 75, 100]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );
};

export default DataGrid;
